// =============================================================================
// src/features/explore/explore-g26-ui.ts — Itens de menu e rotulos de chip do
//   modo Eleicoes Gerais 2026 + busca dentro de menu longo.
//
// Extraido do ExploreUI (explore-ui.ts ja passa de 500 linhas). O menu de
//   candidato de Dep. Estadual tem 278 nomes (TSE, 2026-10-13) — por isso a busca
//   embutida (`attachMenuSearch`), que filtra os itens sem reconstruir o menu.
// SRP: so itens/rotulos/DOM de menu; o estado vem do ExploreMap.
// =============================================================================

import { escapeHtml } from '../../app/dom';
import type { G26Dataset } from './explore-g26-types';
import { g26PartyColor, G26_YEAR_VALUE, G26_CARGO_SHORT } from './explore-g26-constants';
import { g26RankedCandidates, g26CandidateLabel, g26CandidateId, g26IndexOfId, g26PartiesByStateVotes, type G26Selection } from './explore-g26-derive';
import type { ExploreMenuItem } from './explore-menus';

/** Item "2026" do menu de Ano (vem por ultimo, ordem cronologica). */
export const G26_YEAR_MENU_ITEM: ExploreMenuItem = { value: G26_YEAR_VALUE, label: '2026 · Eleições gerais (1º turno)' };

/** Itens do menu de Cargo: nome + quantos candidatos/eleitos. */
export function g26CargoMenuItems(ds: G26Dataset): readonly ExploreMenuItem[] {
  return ds.cargos.map((c) => {
    const eleitos = c.eleitos ? ` · ${c.eleitos} eleitos` : '';
    return { value: c.code, label: `${c.nome} · ${c.candidatos} candidatos${eleitos}` };
  });
}

/** Itens do menu de Candidato (todos os do cargo, ordem oficial) + "Mais votado". */
export function g26CandidateMenuItems(sel: G26Selection): readonly ExploreMenuItem[] {
  return [
    { value: '', label: 'Mais votado em cada município' },
    ...g26RankedCandidates(sel).map((r) => ({ value: g26CandidateId(r.index), label: escapeHtml(g26CandidateLabel(r)), swatch: g26PartyColor(r.cand.partido) })),
  ];
}

/** Itens do menu de Partido / 2º colocado (siglas do cargo, pelos votos no AM). */
export function g26PartyMenuItems(sel: G26Selection, allLabel: string): readonly ExploreMenuItem[] {
  return [
    { value: '', label: allLabel },
    ...g26PartiesByStateVotes(sel).map((sigla) => ({ value: sigla, label: escapeHtml(sigla), swatch: g26PartyColor(sigla) })),
  ];
}

/** Nome do candidato 2026 selecionado (chip) ou null. */
export function g26CandidateChipLabel(ds: G26Dataset, id: string | null): string | null {
  const index = g26IndexOfId(id);
  return index === null ? null : ds.candidatos[index]?.nome ?? null;
}

/** Rotulo do chip de Cargo. */
export function g26CargoChipLabel(sel: G26Selection): string {
  return G26_CARGO_SHORT[sel.cargo];
}

/** Texto sem acento/caixa p/ comparar na busca. */
function foldText(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Mostra so os itens cujo texto contem a busca (vazio = todos). */
export function filterMenuItems(menu: HTMLElement, query: string): void {
  const needle = foldText(query.trim());
  menu.querySelectorAll<HTMLElement>('.exp-menu-item').forEach((item) => {
    item.hidden = needle !== '' && !foldText(String(item.textContent)).includes(needle);
  });
}

/**
 * Poe um campo de busca no topo do menu (filtra os itens ao digitar). Clique/tecla
 * no campo nao fecham o menu (o dismiss global ouve `click` no document).
 */
export function attachMenuSearch(menu: HTMLElement, placeholder: string): HTMLInputElement {
  const input = document.createElement('input');
  input.type = 'search';
  input.className = 'exp-menu-search';
  input.placeholder = placeholder;
  input.setAttribute('aria-label', placeholder);
  input.addEventListener('click', (ev) => ev.stopPropagation());
  input.addEventListener('input', () => filterMenuItems(menu, input.value));
  menu.prepend(input);
  return input;
}
